import React from 'react';
import '../styles/legal.css';

const DPA = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1 className="legal-title">ADGRO DATA PROCESSING ADDENDUM</h1>
        <p className="legal-subtitle">(DPA)</p>

        <div className="legal-meta">
          <p><strong>Effective Date:</strong> January 18, 2026</p>
        </div>

        <p className="legal-intro">
          This Data Processing Addendum ("DPA") forms part of the Terms of Service between Rhombus Labs Inc. ("Rhombus Labs" or "Processor") and the retailer or business ("Customer" or "Controller") and governs the processing of personal information. Adgro is a product developed and operated by Rhombus Labs Inc.
        </p>

        <section className="legal-section">
          <h2>1. DEFINITIONS</h2>
          <p>In this DPA:</p>
          <ul>
            <li><strong>"Controller"</strong> means the Customer, who determines the purposes and means of processing personal information.</li>
            <li><strong>"Processor"</strong> means Adgro, who processes personal information on behalf of the Controller.</li>
            <li><strong>"Personal Information"</strong> has the meaning given under PIPEDA and includes any information about an identifiable individual.</li>
            <li><strong>"Data Breach"</strong> means any unauthorized access, acquisition, use, or disclosure of personal information that compromises its security or confidentiality.</li>
            <li><strong>"Sub-processor"</strong> means any third party engaged by Adgro to process personal information on behalf of the Controller.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>2. ROLES AND RESPONSIBILITIES</h2>

          <h3>2.1 Customer (Controller)</h3>
          <p>Customer acknowledges and agrees that:</p>
          <ul>
            <li>Customer is the Controller of all personal information processed through the Service.</li>
            <li>Customer is solely responsible for determining the lawful basis for processing and obtaining all necessary consents and authorizations.</li>
            <li>Customer is responsible for the accuracy, quality, and legality of personal information provided to Adgro.</li>
            <li>Customer is responsible for compliance with applicable privacy laws, including PIPEDA and provincial privacy legislation.</li>
          </ul>

          <h3>2.2 Adgro (Processor)</h3>
          <p>Adgro agrees that:</p>
          <ul>
            <li>Adgro acts solely as a Processor, processing personal information only on Customer's behalf.</li>
            <li>Adgro will not process personal information for any purpose other than to provide the Service and as instructed by Customer.</li>
            <li>Adgro will not sell, rent, or otherwise disclose personal information for its own commercial purposes.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. PROCESSING INSTRUCTIONS</h2>

          <h3>3.1 Scope of Processing</h3>
          <p>Adgro will process personal information only:</p>
          <ul>
            <li>As necessary to provide the Service described in the Terms of Service</li>
            <li>In accordance with Customer's documented instructions</li>
            <li>As required by applicable law (in which case Adgro will inform Customer unless prohibited by law)</li>
          </ul>

          <h3>3.2 Nature of Processing</h3>
          <p>Processing activities include:</p>
          <ul>
            <li>Collection and storage of advertising campaign data</li>
            <li>Behavioral tracking and audience targeting</li>
            <li>AI-assisted analysis for campaign optimization</li>
            <li>Ad delivery and performance tracking</li>
            <li>Analytics and reporting</li>
          </ul>

          <h3>3.3 Categories of Data Subjects</h3>
          <ul>
            <li>End consumers and website visitors (of Customer's properties)</li>
            <li>Customer's staff and authorized users</li>
          </ul>

          <h3>3.4 Types of Personal Information</h3>
          <ul>
            <li>Device identifiers and cookie data</li>
            <li>IP addresses and location data</li>
            <li>Browsing behavior and interaction data</li>
            <li>Ad engagement data (impressions, clicks, conversions)</li>
            <li>Audience segment and demographic information</li>
            <li>Contact information (for Customer staff)</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. SECURITY SAFEGUARDS</h2>
          <p>Adgro implements and maintains appropriate administrative, technical, and physical safeguards to protect personal information against unauthorized access, disclosure, alteration, or destruction.</p>

          <h3>4.1 Technical Safeguards</h3>
          <ul>
            <li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256)</li>
            <li>Secure authentication mechanisms</li>
            <li>Access controls and role-based permissions</li>
            <li>Regular security testing and vulnerability assessments</li>
            <li>Intrusion detection and monitoring</li>
          </ul>

          <h3>4.2 Administrative Safeguards</h3>
          <ul>
            <li>Employee training on privacy and security</li>
            <li>Confidentiality agreements with personnel</li>
            <li>Documented security policies and procedures</li>
            <li>Incident response plan</li>
          </ul>

          <h3>4.3 Physical Safeguards</h3>
          <ul>
            <li>Secure data center facilities</li>
            <li>Physical access controls</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. SUB-PROCESSORS</h2>

          <h3>5.1 Authorization</h3>
          <p>Customer authorizes Adgro to engage Sub-processors to assist in providing the Service, subject to the requirements of this Section.</p>

          <h3>5.2 Sub-processor Obligations</h3>
          <p>Adgro will:</p>
          <ul>
            <li>Enter into written agreements with Sub-processors that impose data protection obligations equivalent to those in this DPA</li>
            <li>Remain responsible for the acts and omissions of Sub-processors</li>
            <li>Maintain a list of current Sub-processors available upon request</li>
          </ul>

          <h3>5.3 Current Sub-processors</h3>
          <div className="legal-table-container">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Sub-processor</th>
                  <th>Purpose</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Amazon Web Services</td>
                  <td>Cloud hosting</td>
                  <td>Canada / USA</td>
                </tr>
                <tr>
                  <td>Google Analytics</td>
                  <td>Analytics services</td>
                  <td>USA</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="legal-section">
          <h2>6. DATA BREACH NOTIFICATION</h2>

          <h3>6.1 Notification</h3>
          <p>Adgro will notify Customer without unreasonable delay (and in any event within 72 hours) upon becoming aware of a Data Breach involving Customer's personal information.</p>

          <h3>6.2 Information Provided</h3>
          <p>Notification will include, to the extent known:</p>
          <ul>
            <li>Description of the nature of the breach</li>
            <li>Categories and approximate number of individuals affected</li>
            <li>Categories and approximate number of records concerned</li>
            <li>Likely consequences of the breach</li>
            <li>Measures taken or proposed to address the breach</li>
          </ul>

          <h3>6.3 Cooperation</h3>
          <p>Adgro will cooperate with Customer and provide reasonable assistance to investigate the breach and fulfill Customer's notification obligations to affected individuals and regulators.</p>
        </section>

        <section className="legal-section">
          <h2>7. DATA SUBJECT REQUESTS</h2>
          <p>If Adgro receives a request from an individual regarding their personal information, Adgro will:</p>
          <ul>
            <li>Promptly notify Customer of the request</li>
            <li>Direct the individual to contact Customer</li>
            <li>Provide reasonable assistance to Customer in responding to the request</li>
          </ul>
          <p>Customer is responsible for responding to data subject requests and determining the appropriate response.</p>
        </section>

        <section className="legal-section">
          <h2>8. AUDITS AND ASSESSMENTS</h2>
          <p>Upon reasonable request and subject to confidentiality obligations, Adgro will:</p>
          <ul>
            <li>Provide Customer with information necessary to demonstrate compliance with this DPA</li>
            <li>Make available security certifications and audit reports (if available)</li>
            <li>Cooperate with audits conducted by Customer or a third-party auditor, with reasonable advance notice and during normal business hours</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9. DATA DELETION AND RETURN</h2>

          <h3>9.1 Upon Termination</h3>
          <p>Upon termination or expiration of the Terms of Service, Adgro will:</p>
          <ul>
            <li>At Customer's request, return or provide Customer with a copy of Customer Data in a commonly used format</li>
            <li>Delete Customer Data within 90 days of termination, unless retention is required by applicable law</li>
          </ul>

          <h3>9.2 Certification</h3>
          <p>Upon request, Adgro will provide written certification of data deletion.</p>
        </section>

        <section className="legal-section">
          <h2>10. APPLICABLE LAW</h2>
          <p>This DPA is designed to comply with:</p>
          <ul>
            <li>Personal Information Protection and Electronic Documents Act (PIPEDA)</li>
            <li>Personal Information Protection Act (PIPA) - British Columbia and Alberta</li>
            <li>Other applicable provincial privacy legislation</li>
          </ul>
          <p>This DPA is governed by the laws specified in the Terms of Service.</p>
        </section>

        <section className="legal-section">
          <h2>11. CONTACT</h2>
          <p>For questions about this DPA, please contact:</p>
          <div className="legal-contact">
            <p><strong>Rhombus Labs Inc.</strong></p>
            <p>Email: <a href="mailto:support@adgro.ca">support@adgro.ca</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DPA;
